import { TRefElementInstance, RefElement } from 'widgets/toolbox/RefElement';
import { TInputSelectInstance } from 'widgets/forms/InputSelect';
import StickySidebar from 'sticky-sidebar';
import { isBase64Encoded, extractPath, getHeaderHeight } from '../utils';
import { TSizeTabsInstance } from 'core/global/CoreSizeTabs';
import { TAccordionInstance } from 'widgets/global/Accordion';
import { TCoreAccordionItemMixinInstance } from '../global/CoreAccordionItemMixin';
import { isExtraLargeView, isMediumViewAndDown, isSmallView } from 'widgets/toolbox/viewtype';
import { getScrollPosition, onScroll } from 'widgets/toolbox/scroll';
import { scrollWindowTo } from 'widgets/toolbox/scroll';
import { checkBackForward } from 'core/toolbox/history';
import sessionStorageWrapper from 'core/toolbox/sessionStorageWrapper';

type THistory = {
    showAjaxUrl: string;
    showUrl: string;
};
type TButton = ReturnType<typeof import('widgets/global/Button').default>;
type TButtonInstance = InstanceType<TButton>;

declare global {
    interface Window {
        productPositions?: Record<string, number>;
    }
}

const BACK_ELEMENT_STORAGE_KEY_PREFIX = 'plp:back-element-selector:';

/**
 * @param ProductListingMgrClassCreator Base widget for extending
 * @returns ProductListingMgr widget
 */
function CoreProductListingMgrClassCreator(ProductListingMgrClassCreator: import('widgets/search/ProductListingMgr').TProductListingMgr) {
    class CoreProductListingMgr extends ProductListingMgrClassCreator {
        selectedRefinementsHeight!: number;

        isSelectedRefinementsExpanded = false;

        stickySidebar!: StickySidebar;

        static readonly REFINEMENTS_PANEL_XL_ID = 'refinements-panel-xl';

        currentRefIdx: number = 0;

        currentRefAccordionContentId: string = '';

        currentRefTablistTabId: string = '';

        updateViewEvent: string = 'plp.updateView';

        prefs() {
            return {
                classesAppliedFilterItem: '.b-applied_filters-item',
                appliedRefinementLinkContainer: '.b-refinements_accordion-content',
                classesSticky: 'm-sticky',
                resultsCount: 0,
                analyticsAllRefinementsLabel: '',
                classRefinementSwatch: 'b-refinement_swatch',
                classRefinementTablistSwatch: 'b-refinement_tablist-swatch',
                classRefinementTablistTab: '.b-variations_item-tab',
                classAppliedFilterRemoveButton: 'b-applied_filters-remove_button',
                classRefinementAccordiontItem: '.b-refinements_accordion-item',
                optimizelyFeatureFlagKey: '',
                ...super.prefs()
            };
        }

        init() {
            super.init();

            this.initAppliedFiltersItemsVisibility();

            if (isExtraLargeView()) {
                this.initStickySidebar();
            }

            this.eventBus().on(this.updateViewEvent, 'initAppliedFiltersItemsVisibility');
            this.eventBus().on(this.updateViewEvent, 'adjustPromotionalRows');
            this.eventBus().on('tile.product.link.click', 'onProductLinkClick');

            this.restoreScrollFromSessionStorage();

            this.setScrollPosition();

            this.handleScroll();

            this.adjustPromotionalRows();

            this.initOptimizely();
        }

        /**
         * Inits Optimizely decisions for PLP,
         * triggers swipe mode for tiles.
         */
        async initOptimizely() {
            const optimizelyFeatureFlagKey = this.prefs().optimizelyFeatureFlagKey;

            if (optimizelyFeatureFlagKey) {
                const decision = (await this.callIfExists('decideFeatureFlagVariation', optimizelyFeatureFlagKey)) as string;

                if (decision === 'treatment_1') {
                    this.eventBus().emit('optimizely.viewtype.change');
                }
            }
        }

        adjustPromotionalRows() {
            const rowSize = isSmallView() ? 2 : 3;
            const grid = this.ref('grid')?.get();
            const gridTiles = grid?.querySelectorAll('.grid-tile');
            const promoRowsWrapper = document.querySelector<HTMLElement>('.promotional-rows-wrapper');
            const promotionalRows = promoRowsWrapper?.querySelectorAll<HTMLElement>('.promotional-row:not(.m-rendered)') || [];

            if (gridTiles?.length) {
                let rowPos;
                const rowsToInsert = Array.from(promotionalRows).filter(row => {
                    rowPos = (Number(row?.dataset?.position ?? 1) - 1) * rowSize;
                    row.dataset.mappingPosition = String(rowPos);

                    return gridTiles.length >= rowPos && rowPos >= 0;
                });

                let refNode;

                rowsToInsert.forEach(row => {
                    refNode = gridTiles[Number(row.dataset.mappingPosition)] ?? null;

                    grid?.querySelector('.l-plp_grid-tiles')?.insertBefore(row, refNode);
                    row.classList.add('m-rendered');
                    row.removeAttribute('aria-hidden');
                });
            }
        }

        /**
         * @description Restores scroll position to the last viewed product tile after back navigation.
         * It retrieves the stored selector from sessionStorage and scrolls to the corresponding element.
         * This is useful for maintaining user context when navigating back to the product listing page.
         */
        restoreScrollFromSessionStorage() {
            if (this.prefs().firstPage) {
                return;
            }

            checkBackForward().then(isBF => {
                if (!isBF) {
                    return;
                }

                const selector = sessionStorageWrapper.getItem(BACK_ELEMENT_STORAGE_KEY_PREFIX + window.location.pathname);

                if (selector) {
                    const element = this.ref('grid').get()?.querySelector(selector);

                    if (element) {
                        scrollWindowTo(element as HTMLElement, true);
                    }

                    sessionStorageWrapper.removeItem(BACK_ELEMENT_STORAGE_KEY_PREFIX + window.location.pathname);
                }
            });
        }

        /**
         * @description Loads more or previous products in the product listing grid.
         * @param button - The button element that triggered the action.
         * @param isLoadMore - A flag indicating whether to load more products (`true`) or previous products (`false`).
         * @returns A promise that resolves when the product chunk is loaded and appended to the grid.
         */
        loadMorePrev(button: TRefElementInstance, isLoadMore: boolean) {
            let href = button.data('show-url') as string;

            href = isBase64Encoded(href) ? atob(href) : href;

            const history = {
                showUrl: button.attr('href') || button.data('history-url'),
                showAjaxUrl: button.data('show-ajax-url')
            } as THistory;

            history.showUrl = isBase64Encoded(history.showUrl) ? atob(history.showUrl) : history.showUrl;
            history.showAjaxUrl = isBase64Encoded(history.showAjaxUrl) ? atob(history.showAjaxUrl) : history.showAjaxUrl;

            let url = '';

            if (typeof href === 'string') {
                url = href;
            }

            const loadBlock = isLoadMore ? 'loadMoreBlock' : 'loadPrevBlock';

            return this.loadNewChunk(url, loadBlock, history);
        }

        /**
         * Loads a new chunk of product data and updates the product grid accordingly.
         *
         * @param url - The URL to fetch the new chunk of product data.
         * @param loadBlock - The identifier for the block being loaded (e.g., 'loadMoreBlock').
         * @param history - The history object used for navigation or state management.
         *
         * The method determines whether the new chunk is being appended or prepended to the product grid
         * based on the `loadBlock` parameter. It updates the product grid, adjusts the scroll position,
         * removes unnecessary elements, triggers accessibility alerts, and updates the sticky sidebar.
         */
        loadNewChunk(url: string, loadBlock: string, history) {
            const lastIndex = this.getProductTiles().length;
            const isLoadMore = loadBlock === 'loadMoreBlock';

            return this.loadChunk(
                url,
                response => {
                    if (typeof response === 'string') {
                        const gridElements = this.parseProductGridUpdateResponse(response);
                        const productGrid = this.ref('productGrid');
                        const contentHeight = productGrid.get()?.offsetHeight || 0;

                        if (isLoadMore) {
                            productGrid.append(gridElements);
                            this.mergeGridTiles();
                        } else {
                            productGrid.prepend(gridElements);
                            this.mergeGridTiles();
                            this.adjustScrollPosition(contentHeight);
                        }

                        this.ref(loadBlock).remove();
                        this.accessibilityAlert(this.prefs().accessibilityAlerts.productlistupdated);
                        this.focusProductTilesByIndex(isLoadMore ? lastIndex : this.getProductTiles().length - (lastIndex + 1));
                        this.updateStickySidebar();
                    }
                },
                true,
                history
            );
        }

        /**
         * @description Merges every `.l-plp_grid-tiles` batch inside the product grid into the first one so
         * load-more/load-prev keep a single continuous grid. Without this, pinned products expand a page to
         * a tile count that is not a multiple of the column count, leaving the batch's last row partial and
         * showing a gap before the next batch's grid (LP-14213).
         */
        mergeGridTiles() {
            const productGrid = this.ref('productGrid').get();

            if (!productGrid) {
                return;
            }

            const grids = Array.from(productGrid.querySelectorAll<HTMLElement>('.l-plp_grid-tiles'));

            if (grids.length < 2) {
                return;
            }

            const target = grids[0];

            grids.slice(1).forEach(grid => {
                while (grid.firstElementChild) {
                    target.appendChild(grid.firstElementChild);
                }

                grid.remove();
            });
        }

        /**
         * Handles the click event on product links within the product listing.
         * It stores the selector of the clicked product tile in sessionStorage for later use,
         * allowing for back navigation to the specific product tile.
         *
         * @param link - The link element that was clicked.
         */
        onProductLinkClick(link: TRefElementInstance) {
            const linkElement = link.get();

            if (!linkElement || !this.ref('self').get()?.contains(linkElement)) {
                return;
            }

            const productTileElement = linkElement.closest('[data-widget="productTile"]');

            if (!productTileElement) {
                return;
            }

            const productTileProductId = productTileElement.getAttribute('data-product-id');
            const selector = productTileProductId && `[data-widget="productTile"][data-product-id="${productTileProductId}"]`;

            if (!selector) {
                return;
            }

            // Store the selector in sessionStorage to be used later for back navigation
            sessionStorageWrapper.setItem(BACK_ELEMENT_STORAGE_KEY_PREFIX + window.location.pathname, selector);
        }

        /**
         * Adjusts the scroll position of the window based on the difference between the current content height
         * and the new content height of the product grid. This ensures that the user's scroll position remains
         * consistent after content updates.
         *
         * @param currentContentHeight - The height of the content before the update.
         */
        adjustScrollPosition(currentContentHeight: number) {
            const scrollPosition = getScrollPosition();
            const newContentHeight = this.ref('productGrid').get()?.offsetHeight || 0;

            window.scrollTo({
                top: scrollPosition + newContentHeight - currentContentHeight,
                left: 0,
                behavior: 'instant'
            });
        }

        /**
         * @description Load more products
         * @listens dom#click
         * @param button Target element
         */
        loadMore(button: TRefElementInstance) {
            return this.loadMorePrev(button, true);
        }

        /**
         * @description Load previous products
         * @listens dom#click
         * @param button Target element
         */
        loadPrev(button: TRefElementInstance) {
            return this.loadMorePrev(button, false);
        }

        /**
         * @description Initializes the visibility of applied filter items.
         *
         * This method checks the height of the applied filters list and determines if the "Show More" button
         * is expanded. It then toggles the visibility of the filter items based on these values.
         */
        initAppliedFiltersItemsVisibility() {
            this.selectedRefinementsHeight = this.ref('appliedFiltersList').get()?.offsetHeight ?? 0;
            this.isSelectedRefinementsExpanded = this.ref('showMoreButtonContainer').attr('aria-expanded') === 'true';
            this.toggleAppliedFiltersItemsVisibility();
        }

        /**
         * Handles the "Show More" functionality for the applied filters list.
         * Toggles the visibility of additional filter items and updates the
         * "Show More" button's state (e.g., aria-expanded attribute).
         */
        handleShowMore() {
            this.ref('appliedFiltersList').toggleClass(this.prefs().classesActiveSection);
            this.ref('showMoreButtonContainer')
                .toggleClass(this.prefs().classesActiveSection)
                .attr('aria-expanded', this.isSelectedRefinementsExpanded ? 'false' : 'true');
            this.isSelectedRefinementsExpanded = !this.isSelectedRefinementsExpanded;
            this.toggleAppliedFiltersItemsVisibility();
        }

        /**
         * Toggles the visibility of applied filter items based on their position and the current state of the "Show More" button.
         *
         * This method checks whether the "Show More" button is expanded or collapsed by reading its `aria-expanded` attribute.
         * It then iterates through the applied filter items and sets their `aria-hidden` attribute to control their visibility.
         * Items that exceed the height of `selectedRefinementsHeight` are hidden or shown accordingly.
         */
        toggleAppliedFiltersItemsVisibility() {
            const items = this.ref('appliedFiltersList').get()?.querySelectorAll(this.prefs().classesAppliedFilterItem);
            let hasHiddenItems = false;

            if (items) {
                items.forEach((item, index) => {
                    const isOverflow = (item as HTMLElement).offsetTop + (item as HTMLElement).offsetHeight > this.selectedRefinementsHeight;
                    const isLast = index === items.length - 1;

                    if (isOverflow && !isLast) {
                        hasHiddenItems = true;
                        item.setAttribute('aria-hidden', this.isSelectedRefinementsExpanded ? 'false' : 'true');
                    } else if (isOverflow && isLast) {
                        this.ref('appliedFiltersList').get()?.classList.toggle(this.prefs().classesActiveSection, !hasHiddenItems);
                    }
                });
            }

            this.ref('clearAllListButton').toggle(!hasHiddenItems);
            this.ref('showMoreButtonContainer').toggle(hasHiddenItems);
        }

        /**
         * @description Refresh widget handler
         */
        onRefresh() {
            super.onRefresh();

            this.initAppliedFiltersItemsVisibility();
        }

        /**
         * @description No Result Combination handler
         */
        handleNoResult() {
            super.handleNoResult();

            this.getById('refinement-panel', panel => panel.ref('dialog').attr('aria-busy', 'false'));
        }

        /**
         * @description Mark product grid as busy element
         */
        busy() {
            super.busy();

            if (isMediumViewAndDown()) {
                this.getById(CoreProductListingMgr.REFINEMENTS_PANEL_XL_ID, refinementsPanel => {
                    if (refinementsPanel) {
                        refinementsPanel.ref('dialog').addClass(this.prefs().classesBusy).attr('aria-busy', 'true');
                    }
                });
            }
        }

        /**
         * @description Mark product grid as ready for interaction element
         */
        unbusy() {
            super.unbusy();
            this.getById(CoreProductListingMgr.REFINEMENTS_PANEL_XL_ID, refinementsPanel => {
                if (refinementsPanel) {
                    refinementsPanel.ref('dialog').removeClass(this.prefs().classesBusy).attr('aria-busy', false);
                }
            });
        }

        /**
         * @description Initializes a sticky sidebar for the PLP (Product Listing Page).
         *
         * This method creates a new StickySidebar instance if the sidebar element exists
         * in the DOM. It also registers event listeners for accordion interactions
         * to update the sticky sidebar when content changes.
         */
        initStickySidebar(reInitStickySidebar = false) {
            let plpSidebar;

            this.getById(CoreProductListingMgr.REFINEMENTS_PANEL_XL_ID, refinementsPanel => {
                if (refinementsPanel) {
                    plpSidebar = refinementsPanel.ref('plpSidebar').get();
                }
            });

            if (plpSidebar) {
                this.stickySidebar = new StickySidebar(plpSidebar, {
                    topSpacing: 120,
                    stickyClass: this.prefs().classesSticky
                });
            }

            if (!reInitStickySidebar) {
                this.eventBus().on('toggle.accordion.item', 'updateStickySidebar');
                this.eventBus().on('update.accordion.item.height', 'updateStickySidebar');
                this.eventBus().on('carousel.loaded', 'updateStickySidebar');
                this.eventBus().on('recommendations.inserted', 'reInitStickySidebar');
            }
        }

        /**
         * @description Updates the sticky sidebar with a delay.
         *
         * This method introduces a delay of 400ms before updating the sticky sidebar,
         * allowing for any animations to complete before the sidebar position is recalculated.
         * If no sticky sidebar exists, this method has no effect.
         */
        updateStickySidebar() {
            const animationDelay = 400;

            setTimeout(() => {
                if (this.stickySidebar) {
                    this.stickySidebar.updateSticky();
                }
            }, animationDelay);
        }

        /**
         * Reinitializes the sticky sidebar component.
         *
         * If the sticky sidebar instance exists, it is destroyed and reinitialized
         * after a short delay. This ensures that the sticky sidebar is properly
         * updated and functional, especially after dynamic changes to the layout.
         */
        reInitStickySidebar() {
            if (this.stickySidebar) {
                this.stickySidebar.destroy();

                setTimeout(() => {
                    this.initStickySidebar(true);
                }, 300);
            }
        }

        /**
         * @description Update Grid
         * @listens dom#change
         * @param select Target element
         */
        updateGrid(select: TInputSelectInstance) {
            this.eventBus().emit('impressions.reset');

            const selectedSortingValue = select.getSelectedOptions()?.val();
            let analytics;

            if (selectedSortingValue) {
                analytics = {
                    event: 'plp.sorting.change',
                    selectedSorting: selectedSortingValue
                };
            }

            return super.updateGrid(select).then(() => {
                this.pushAnalyticsEvent(analytics);

                const headerHeight = getHeaderHeight();
                const productGridElement = this.ref('grid').get();

                if (productGridElement && productGridElement.offsetTop - window.scrollY < headerHeight) {
                    scrollWindowTo(this.ref('grid').get(), true);
                }
            });
        }

        /**
         * @description Focuses the filter element by its index.
         * @param section - The section element containing the filter elements.
         * @param buttonClass - The class of the button to focus on (default is '.b-applied_filters-remove_button').
         * If no section is provided, it focuses the element based on the current index and content ID.
         */
        focusFilterElementByIndex(section?: HTMLElement, buttonClass?: string) {
            let isFocusDisabled = false;
            const headerHeight = getHeaderHeight();
            const productGridElement = this.ref('grid').get();

            if (productGridElement && productGridElement.offsetTop - window.scrollY >= headerHeight) {
                isFocusDisabled = true;
            }

            if (!section) {
                this.ref('grid').focus(isFocusDisabled);
            }

            const indexToFocus = Math.min(this.currentRefIdx + 1, section?.querySelectorAll('li')?.length || 1);
            const isTablistItem = buttonClass === this.prefs().classRefinementTablistSwatch;
            const sectionContent = isTablistItem ? section?.querySelector(`#${this.currentRefTablistTabId}`) : section;

            const el = sectionContent?.querySelector(`li:nth-child(${indexToFocus}) .${buttonClass}`);
            const fallbackEl = sectionContent?.querySelector(`li:nth-child(${1}) .${buttonClass}`);

            if (isTablistItem) {
                (sectionContent as HTMLElement).focus({ preventScroll: isFocusDisabled });
            }

            const targetEl = isSmallView() || (el as HTMLElement)?.classList.contains('js-hidden-refinement-swatch') ? fallbackEl : el;

            (targetEl as HTMLElement | null)?.focus({ preventScroll: isFocusDisabled });

            this.currentRefIdx = 0;
            this.currentRefAccordionContentId = '';
            this.currentRefTablistTabId = '';
        }

        /**
         * @description Update View
         * @listens dom#click
         * @param button Target element
         */
        private getUpdateViewAnalytics(button: TRefElementInstance) {
            const analytics: { event?: string; [key: string]: unknown } = {
                event: button.data('analyticsEvent') as string,
                analyticsRefinementLabel: button.data('analyticsRefinementLabel')
            };

            if (analytics.event === 'plp.filter.remove.all') {
                analytics.analyticsAllRefinementsLabel = this.prefs().analyticsAllRefinementsLabel;
            }

            if (analytics.event?.includes('plp.filter.')) {
                this.eventBus().emit('impressions.reset');
            }

            return analytics;
        }

        private getUpdateViewAlertMessage(button: TRefElementInstance) {
            const refinementSelected = button.data('checked');

            return refinementSelected ? this.prefs().accessibilityAlerts.filterremoved : this.prefs().accessibilityAlerts.filtersapplied;
        }

        private handleAccordionFocus(
            buttonRef: TRefElementInstance,
            btnContainer: HTMLElement | null,
            refinementsAccordion: TAccordionInstance,
            currentItem: TCoreAccordionItemMixinInstance
        ) {
            if (this.currentRefAccordionContentId === currentItem.ref('self').get()?.id) {
                if (currentItem?.isPanelOpen === false) {
                    currentItem.refs.accordionItemBtn?.focus();
                } else {
                    this.callIfExists('focusFirstDescendant', btnContainer);
                }
            } else {
                let section = this.ref('appliedFiltersList').get();
                let buttonClass = this.prefs().classAppliedFilterRemoveButton;

                if (buttonRef.hasClass(this.prefs().classRefinementSwatch)) {
                    const sectionContainer = refinementsAccordion.items.find(item => item.id === this.currentRefAccordionContentId);

                    section = sectionContainer?.ref('accordionItemPanel').get();
                    buttonClass = buttonRef.hasClass(this.prefs().classRefinementTablistSwatch)
                        ? this.prefs().classRefinementTablistSwatch
                        : this.prefs().classRefinementSwatch;
                }

                this.focusFilterElementByIndex(section, buttonClass);
            }
        }

        updateView(button: TRefElementInstance) {
            let updateURL = String(button.data('href'));
            const buttonRef = ('ref' in button && typeof button.ref === 'function' && (button as unknown as TButtonInstance).ref('self')) || button;
            const btnContainer =
                buttonRef instanceof RefElement
                    ? (buttonRef.get()?.closest(this.prefs().appliedRefinementLinkContainer) as HTMLElement | null)
                    : null;

            updateURL = isBase64Encoded(updateURL) ? atob(updateURL) : updateURL;

            const alertMessage = this.getUpdateViewAlertMessage(button);
            const analytics = this.getUpdateViewAnalytics(button);

            this.setCurrentRefinementIndices(buttonRef);

            if (button.data('gtm-event-data')) {
                this.handleCategoriesClick(button);
            }

            if (button.data('checked') && btnContainer?.id === 'refinement-size') {
                this.eventBus().emit('sizeVariation.select');
            }

            const savedState: { sizeTabsActivePanel: string | unknown } = {
                sizeTabsActivePanel: undefined
            };

            this.getByIdDeep('size-tabs-wrapper', function (sizeTabs: TSizeTabsInstance) {
                savedState.sizeTabsActivePanel = sizeTabs.activePanel;
            });

            return this.updateByUrl(updateURL, undefined, true)
                .then(() => {
                    this.accessibilityAlert(alertMessage);
                    this.pushAnalyticsEvent(analytics);

                    this.getByIdDeep<TAccordionInstance, void>('refinementsAccordion', refinementsAccordion => {
                        refinementsAccordion.reinit();

                        const currentItem = refinementsAccordion.currentItem as TCoreAccordionItemMixinInstance;

                        this.handleAccordionFocus(buttonRef, btnContainer, refinementsAccordion, currentItem);
                    });
                })
                .finally(() => {
                    this.eventBus().emit(this.updateViewEvent, savedState);
                });
        }

        private setCurrentRefinementIndices(button: TRefElementInstance) {
            this.currentRefIdx = Array.prototype.indexOf.call(
                [...(button.get()?.closest('ul')?.children || [])],
                button.get()?.parentElement || null
            );

            if (button.hasClass(this.prefs().classAppliedFilterRemoveButton)) {
                this.currentRefAccordionContentId = button.get()?.closest(this.prefs().classRefinementAccordiontItem)?.id || '';

                if (!this.currentRefAccordionContentId) {
                    this.currentRefAccordionContentId = 'appliedFiltersList';
                }
            } else if (button.hasClass(this.prefs().classRefinementTablistSwatch)) {
                // If it is a tablist swatch we get the accordion panel parent plus the tablist tab (if applicable)
                this.currentRefAccordionContentId = button.get()?.closest(this.prefs().classRefinementAccordiontItem)?.id || '';
                this.currentRefTablistTabId = button.get()?.closest(this.prefs().classRefinementTablistTab)?.id || '';
            } else if (button.hasClass(this.prefs().classRefinementSwatch)) {
                // If it is a normal swatch we set the accordion panel
                this.currentRefAccordionContentId = button.get()?.closest(this.prefs().classRefinementAccordiontItem)?.id || '';
            } else {
                this.currentRefIdx = 0;
                this.currentRefAccordionContentId = '';
            }
        }

        /**
         * @description After update content hook
         * @param handleHistory Handle history flag
         */
        afterUpdateContent(handleHistory) {
            if (handleHistory) {
                this.handleHistory();

                if (!isSmallView()) {
                    const headerHeight = getHeaderHeight();
                    const productGridElement = this.ref('grid').get();

                    if (productGridElement && productGridElement.offsetTop - window.scrollY < headerHeight) {
                        scrollWindowTo(this.ref('grid').get(), true);
                    }
                }
            }
        }

        /**
         * @description Render response string with product list content updates to the Reference Element
         * @param response Response string
         * @param ref Reference Element
         * @param handleHistory Handle history flag
         */
        updateContent(response: string, ref?: TRefElementInstance, handleHistory?: boolean): Promise<string | null> {
            const refEl = ref || this.ref('self');

            return this.render(undefined, undefined, refEl, response)
                .then(() => {
                    this.afterUpdateContent(handleHistory);
                })
                .then(() => response)
                .catch(() => {
                    // If rendering fails, we clear the content and return null
                    if (refEl && refEl instanceof RefElement) {
                        refEl.setHtml('');
                    }

                    return this.render(undefined, undefined, refEl, response)
                        .then(() => {
                            this.afterUpdateContent(handleHistory);
                        })
                        .then(() => response);
                });
        }

        /**
         * @description Pushes an analytics event to the event bus.
         * @param analytics The analytics data to push.
         */
        pushAnalyticsEvent(analytics: { event?: string; [key: string]: unknown } = {}) {
            if (!analytics?.event) {
                return;
            }

            analytics.resultsCount = this.prefs().resultsCount;
            analytics.analyticsAllRefinementsLabel = analytics.analyticsAllRefinementsLabel || this.prefs().analyticsAllRefinementsLabel;
            this.eventBus().emit(analytics.event, analytics);
        }

        handleCategoriesClick(elem: TRefElementInstance) {
            const data: { [key: string]: unknown } = elem.data('gtm-event-data') || {};

            data.url = extractPath(elem.attr('href'));

            this.eventBus().emit('refinement.category.click', data);
        }

        /**
         * @description Set page scroll position
         */
        setScrollPosition() {
            if (!window.location.search || new URLSearchParams(window.location.search).has('q')) {
                return;
            }

            const savedScrollPos = sessionStorageWrapper.getItem('plpScrollPosition');

            if (savedScrollPos) {
                window.scrollTo(0, parseInt(savedScrollPos, 10));
            }
        }

        /**
         * @description Handle scroll event to save scroll position on session storage
         */
        handleScroll() {
            onScroll(() => {
                const productGridElement = this.ref('productGrid').get();

                if (productGridElement) {
                    const gridTiles = Array.from(productGridElement.getElementsByClassName('l-plp_grid-tiles') || []) as Array<HTMLElement>;

                    // Calculate offset of all tiles that are above the current scroll position
                    const offset = gridTiles
                        .filter(element => {
                            return !(element.offsetTop <= window.scrollY && element.offsetTop + element.offsetHeight > window.scrollY);
                        })
                        .reduce((acc, element) => {
                            return acc + element.offsetHeight;
                        }, 0);

                    sessionStorageWrapper.setItem('plpScrollPosition', String(window.scrollY - offset));
                }
            });
        }
    }

    return CoreProductListingMgr;
}

export type TCoreProductListingMgr = ReturnType<typeof CoreProductListingMgrClassCreator>;

export type TCoreProductListingMgrInstance = InstanceType<TCoreProductListingMgr>;

export default CoreProductListingMgrClassCreator;
